import { ChevronSmallDownIcon as CentralChevronSmallDownIcon } from "@wags/ui/product-icons.tsx";
import { cn } from "@wags/ui/ui-utils";
import { useId, useState } from "react";
import { FaqAskRow } from "./faq-ask-row";
import {
    type FaqActiveAsk,
    type FaqAskSource,
    FaqKitsAnswer,
} from "./faq-kits-answer";
import { compact, joinParts, type ListingData } from "./listing-section-data";
import {
    listingContactAnswers,
    type FeeInclusions,
} from "./listing-contact-answers";

type Faq = { q: string; a: string; provenance?: string; ask?: string };

export type FaqKits = {
    onAsk: (question: string, source: FaqAskSource) => void;
    onContinue: () => void;
    active?: FaqActiveAsk;
};

function listingProvenance(provider: string | undefined) {
    return provider ? `From ${provider}'s listing` : undefined;
}

function derivedFaqs(
    data: ListingData,
    name: string,
    inclusions?: FeeInclusions,
): Faq[] {
    const provider = data.providerName;
    const sizeQuestion = `How big will ${name} get?`;
    const answers = listingContactAnswers({ data, inclusions });

    return compact<Faq>([
        data.weightRange
            ? {
                  q: sizeQuestion,
                  a: `Grown, expect around ${data.weightRange}.`,
              }
            : data.sizeLabel && {
                  q: sizeQuestion,
                  a: `The listing gives a size class — ${data.sizeLabel} — but does not include an adult weight range.`,
                  ask: `How much do ${data.sizeLabel.toLowerCase()} dogs usually weigh grown?`,
              },
        (data.coat || data.exercise) && {
            q: `What's day-to-day life with ${name} like?`,
            a: `${data.breedName ? `Typical ${data.breedName} guidance: ` : "Typical breed guidance: "}${joinParts([data.coat, data.exercise]) ?? ""}.`,
            provenance: "From the breed profile",
        },
        data.goodWith?.length
            ? {
                  q: `Who does ${name} get along with?`,
                  a: `The listing marks ${name} as good with ${data.goodWith.join(", ").toLowerCase()}. Anything not on that list is unknown, not a no.`,
                  provenance: listingProvenance(provider),
                  ask: `What doesn't the listing say about who ${name} gets along with?`,
              }
            : undefined,
        data.transports?.length
            ? {
                  q: `How would we get ${name} home?`,
                  a: data.transports
                      .map(
                          (t) =>
                              joinParts([t.label, t.sub, t.price]) ?? t.label,
                      )
                      .join(" · "),
              }
            : undefined,
        data.city && {
            q: answers.meeting.question,
            a: answers.meeting.answer,
        },
        data.priceLabel && {
            q: answers.fee.question,
            a: answers.fee.answer,
            provenance: answers.fee.provenance,
            ask: "What does a fee like this usually cover?",
        },
    ]);
}

export function FaqSection({
    data,
    kits,
    inclusions,
}: {
    data: ListingData;
    kits?: FaqKits;
    inclusions?: FeeInclusions;
}) {
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const faqId = useId();

    const name = data.dogName ?? "this pup";
    const authoredFaqs = data.descriptionQa?.filter(
        ({ question, answer }) => question.trim() && answer.trim(),
    );
    const faqs: Faq[] = authoredFaqs?.length
        ? authoredFaqs.map(({ question, answer }) => ({
              q: question,
              a: answer,
          }))
        : derivedFaqs(data, name, inclusions);
    if (faqs.length === 0) return null;
    const active = kits?.active;
    const kitsAnswer = active && kits && (
        <FaqKitsAnswer ask={active} onContinue={kits.onContinue} />
    );

    return (
        <div>
            <h2 className="text-section-title text-doberman-black">
                Common questions
            </h2>
            <div className="mt-1 divide-y divide-doberman-black/10">
                {faqs.map(({ q, a, provenance, ask }, i) => {
                    const open = openIndex === i;
                    const answeringHere =
                        active?.source === "follow_up" &&
                        active.question === ask;
                    return (
                        <div key={`${i}-${q}`} className="py-4 last:pb-1">
                            <button
                                type="button"
                                onClick={() => setOpenIndex(open ? null : i)}
                                aria-expanded={open}
                                aria-controls={`${faqId}-panel-${i}`}
                                className="flex w-full items-start justify-between gap-3 text-left"
                            >
                                <span className="text-body font-semibold text-doberman-black">
                                    {q}
                                </span>
                                <CentralChevronSmallDownIcon
                                    aria-hidden="true"
                                    className={cn(
                                        "mt-0.5 size-5 shrink-0 text-doberman-black/45 transition-transform",
                                        open && "rotate-180",
                                    )}
                                />
                            </button>
                            <div
                                id={`${faqId}-panel-${i}`}
                                aria-hidden={!open}
                                inert={!open}
                                className={cn(
                                    "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.165,0.84,0.44,1)] motion-reduce:transition-none",
                                    open
                                        ? "grid-rows-[1fr]"
                                        : "grid-rows-[0fr]",
                                )}
                            >
                                <div className="overflow-hidden">
                                    <div className="mt-2.5">
                                        <p className="text-body text-doberman-black/70">
                                            {a}
                                        </p>
                                        {provenance && (
                                            <p className="text-caption mt-2.5 text-doberman-black/55">
                                                {provenance}
                                            </p>
                                        )}
                                        {answeringHere
                                            ? kitsAnswer
                                            : ask &&
                                              kits && (
                                                  <button
                                                      type="button"
                                                      onClick={() =>
                                                          kits.onAsk(
                                                              ask,
                                                              "follow_up",
                                                          )
                                                      }
                                                      className="mt-3 rounded-full border border-doberman-black/10 px-3.5 py-2 text-left text-body-small font-medium text-doberman-black transition-transform active:scale-96 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow motion-reduce:transition-none motion-reduce:active:scale-100"
                                                  >
                                                      {ask}
                                                  </button>
                                              )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {kits && (
                    <div className="py-4 last:pb-1">
                        <FaqAskRow
                            label={`Ask about ${name}`}
                            onSubmit={(question) =>
                                kits.onAsk(question, "typed")
                            }
                        />
                        {active?.source === "typed" && kitsAnswer}
                    </div>
                )}
            </div>
        </div>
    );
}
